import { Calendar as CalendarIcon, Search } from "lucide-react";
import { Input } from "../../../../../shadcnComponents/ui/input";
import {
  ScrollArea,
  ScrollBar,
} from "../../../../../shadcnComponents/ui/scroll-area";
import { MetricCard } from "./atoms";
import { Button } from "../../../../../shadcnComponents/ui/button";
import { BiSync } from "react-icons/bi";
import { cn } from "../../../../../lib/utils";
import { Popover } from "../../../../../shadcnComponents/ui/popover";
import { PopoverContent, PopoverTrigger } from "@radix-ui/react-popover";
import { Calendar } from "../../../../../shadcnComponents/ui/calendar";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import React from "react";

export const StatisticsDashboardCard = ({
  MetricData,
}: {
  MetricData: {
    title: string;
    value: string | number;
    active?: boolean;
    isPercentage?: boolean;
  }[];
}) => {
  return (
    <>
      <div className="flex">
        <ScrollArea className="w-1 flex-1">
          <div className="flex gap-4 pb-3">
            {MetricData?.map((item, index) => (
              <MetricCard
                key={index}
                title={item?.title}
                value={item?.value}
                active={item?.active}
                isPercentage={item?.isPercentage}
              />
            ))}
          </div>
          <ScrollBar orientation="horizontal" className="w-full" />
        </ScrollArea>
      </div>
    </>
  );
};

export const SearchBar = ({
  value,
  onValueChange,
  onSearch,
}: {
  value: string;
  onValueChange: (value: string) => void;
  onSearch: () => void;
}) => {
  return (
    <div className="relative">
      <Input
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onSearch();
          }
        }}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        placeholder="Eg: Giveaway Alert!"
        className="bg-transparent border border-[#595B5D] pr-16 w-full max-w-[300px] placeholder:text-[#595B5D] placeholder:text-base text-base"
      />
      <Button
        className="absolute right-0 top-0 bottom-0 px-4 bg-[#595B5D] hover:bg-slate-500 rounded-md grid place-items-center"
        onClick={() => onSearch}
      >
        <Search size={20} />
      </Button>
    </div>
  );
};

export const SyncProfileButton = ({ handleSyncProfile, updatedAt }) => {
  return (
    <div className="flex flex-col justify-end gap-2 ">
      <div className="h-4 md:hidden"></div>
      <Button
        variant="destructive"
        className="p-2 hover:bg-red-800 rounded-md border border-red-800 text-xs md:text-sm"
        onClick={handleSyncProfile}
      >
        <span className="text-white font-inter hidden md:block">
          Sync again
        </span>
        <BiSync size={20} title="sync" className="" />{" "}
      </Button>
      <div className="hidden md:flex  flex-col text-end">
        <span
          className="text-green-500 text-xs font-inter cursor-default"
          title={new Date(updatedAt).toLocaleString()}
        >
          Updated on: {new Date(updatedAt).toDateString()}
        </span>
      </div>
    </div>
  );
};

interface SectionCardProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
  icon?;
  parentClass?: string;
  action?: React.ReactNode;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  children,
  icon,
  title,
  subtitle,
  parentClass,
  action,
}) => {
  return (
    <div
      className={cn(
        "w-full flex p-[16px] flex-col items-start gap-[20px] rounded-[8px] border-[1px] border-solid border-[#262626]",
        parentClass
      )}
    >
      <div className="flex justify-between w-full items-center">
        <div className="flex flex-col items-start gap-0 self-stretch">
          <div className="flex items-start gap-[8px] self-stretch">
            {icon && (
              <span className="text-[#FFF] text-center font-[Inter] text-[15px] not-italic font-normal leading-[20px]">
                {icon}
              </span>
            )}
            <h3 className="text-[#FFF] font-[Inter] text-[14px] not-italic font-semibold leading-[20px] line-clamp-1">
              {title}
            </h3>
          </div>
          <span className="self-stretch text-[#919191] font-[Inter] text-[12px] not-italic font-normal leading-[20px] line-clamp-1">
            {subtitle}
          </span>
        </div>
        {action}
      </div>

      {children}
    </div>
  );
};

interface PickDateBtnProps {
  onClick?: () => void;
  date?: DateRange;
  setDate?: (date: DateRange) => void;
}

export const PickDateBtn = ({ onClick, date, setDate }: PickDateBtnProps) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className="bg-transparent hover:bg-[#CBD5E13B] border-[#CBD5E13B] rounded-md text-sm px-3 py-2 gap-2 flex items-center"
          onClick={onClick}
        >
          <CalendarIcon size={14} />

          <span className="font-[Inter] text-xs not-italic font-normal leading-[normal]">
            {date?.from ? (
              date.to ? (
                <>
                  <span className="hidden md:block">
                    {format(date.from, "LLL dd, y")} -{" "}
                    {format(date.to, "LLL dd, y")}
                  </span>
                  <span className="block md:hidden">
                    {format(date.from, "LLL dd")} - {format(date.to, "LLL dd")}
                  </span>
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto bg-[#171717] border rounded-md border-[#292F35] p-3 m-2 z-30">
        <Calendar
          initialFocus={true}
          mode="range"
          defaultMonth={date?.from}
          selected={date}
          onSelect={setDate}
          numberOfMonths={1}
          disabled={(date) => date > new Date()}
          classNames={{
            day_selected: "bg-[#4b72ff] text-white",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
